"use client";

import { useEffect, useState } from "react";

import { DATA_LABEL_CLS } from "../theme";
import twinStyles from "./glass-twin.module.css";
import { TWIN_SEQUENCE } from "./sequence";
import { useTwinClock } from "./sequence-context";

/**
 * Decorative service riser between the evidence-source card and the Twin
 * instrument, lg+ only. Six dotted stubs rise from the card's column centres
 * into a dotted collector bus; a single riser leaves the filled collector at
 * 75% and ends in an open chevron near the band top (the short datum line
 * above it came out on 2026-10-01). The pulse travel in
 * glass-twin.module.css must match this geometry.
 *
 * The pulse keeps the stage's clock: on the shared clock it joins the loop
 * wherever the sequence stands; on a stage's own clock it rises once, at
 * that clock's pace, when the clock starts.
 */

/** Centres of the six evidence-source grid columns. */
const STUB_CENTRES = [
  "8.333%",
  "25%",
  "41.667%",
  "58.333%",
  "75%",
  "91.667%",
] as const;

/** The riser leaves the bus at the fifth column centre. */
const RISER_X = "75%";

export function EvidenceRiser() {
  const clock = useTwinClock();
  const [sequenceOffset, setSequenceOffset] = useState(0);

  useEffect(() => {
    setSequenceOffset(clock.read() / clock.rate);
  }, [clock]);

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-full h-35 max-lg:hidden"
      aria-hidden="true"
    >
      <div className="absolute top-5 left-3/4 size-[0.55rem] -translate-x-1/2 rotate-45 border-t border-l border-[var(--theme-dark)]" />
      <div className="absolute top-7 bottom-7.5 left-3/4 w-0 -translate-x-1/2 border-l border-dotted border-[var(--rule)]" />
      {clock.running ? (
        <div
          className={`${twinStyles.sourceFlowPulse} absolute bottom-7.5 left-3/4 size-[7px] -translate-x-1/2 translate-y-1/2 rounded-full bg-[var(--theme-dark)]`}
          style={{
            animationDelay: `-${sequenceOffset}s`,
            animationDuration: `${TWIN_SEQUENCE.totalSeconds / clock.rate}s`,
            ...(clock.loops ? null : { animationIterationCount: 1, animationFillMode: "forwards" }),
          }}
        />
      ) : null}
      <span
        className={`absolute top-[3.6rem] left-3/4 -translate-x-1/2 bg-[var(--theme-ground)] px-2 whitespace-nowrap text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
      >
        Read-only evidence path
      </span>
      <div className="absolute right-[8.333%] bottom-7.5 left-[8.333%] border-t border-dotted border-[var(--rule)]" />
      {STUB_CENTRES.map((centre) => (
        <div
          className="absolute bottom-0 h-7.5 w-0 -translate-x-1/2 border-l border-dotted border-[var(--rule)]"
          key={centre}
          style={{ left: centre }}
        />
      ))}
      {STUB_CENTRES.filter((centre) => centre !== RISER_X).map((centre) => (
        <div
          className="absolute bottom-7.5 size-1.5 -translate-x-1/2 translate-y-1/2 border border-[var(--theme-dark)] bg-[var(--theme-ground)]"
          key={centre}
          style={{ left: centre }}
        />
      ))}
      <div className="absolute bottom-7.5 left-3/4 size-2 -translate-x-1/2 translate-y-1/2 bg-[var(--theme-dark)]" />
    </div>
  );
}
